import { useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  AtSign,
  Copy,
  Key,
  KeyRound,
  Lock,
  Mail,
  ShieldCheck,
  UserRound,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Shared, compact building blocks for detail/profile pages            */
/* (EmployeeView, DoctorView, PatientDetail) so they stay identical.   */
/* ------------------------------------------------------------------ */

/* ------------------------------- Tabs ----------------------------- */

export interface ProfileTab<T extends string = string> {
  id: T;
  label: string;
  icon: LucideIcon;
  count?: number;
}

export function TabBar<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: ProfileTab<T>[];
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Profile sections"
      className="flex w-full items-center gap-1 overflow-x-auto rounded-xl border border-border/70 bg-surface p-1 shadow-sm sm:w-auto"
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.id)}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-[0.8125rem] font-medium transition-all ${
              isActive
                ? 'bg-primary/10 text-primary shadow-sm ring-1 ring-inset ring-primary/15'
                : 'text-text-muted hover:bg-background hover:text-text'
            }`}
          >
            <Icon size={14} className={isActive ? 'text-primary' : 'text-text-muted'} />
            {tab.label}
            {typeof tab.count === 'number' && (
              <span
                className={`ml-0.5 rounded-full px-1.5 py-0.5 text-[0.625rem] font-semibold tabular-nums ${
                  isActive ? 'bg-primary/15 text-primary' : 'bg-background text-text-muted'
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------- Section card ------------------------ */

export function SectionCard({
  icon: Icon,
  title,
  subtitle,
  action,
  children,
  className = '',
}: {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`overflow-hidden rounded-2xl border border-border/70 bg-surface shadow-sm ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 px-4 py-3 sm:px-5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icon size={16} />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-[0.8125rem] font-semibold text-text sm:text-sm">{title}</h2>
            {subtitle && <p className="truncate text-[0.6875rem] text-text-muted">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

/* ------------------------------ Rows ------------------------------ */

export function DetailRow({
  label,
  value,
  mono = false,
  className = '',
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`flex items-start justify-between gap-4 border-b border-border/50 py-2 last:border-0 ${className}`}
    >
      <span className="shrink-0 text-xs text-text-muted sm:text-[0.8125rem]">{label}</span>
      <span
        className={`min-w-0 break-words text-right text-[0.8125rem] font-medium text-text ${
          mono ? 'font-mono' : ''
        }`}
      >
        {value}
      </span>
    </div>
  );
}

/* --------------------------- Info tiles --------------------------- */

export function InfoTile({
  icon: Icon,
  label,
  value,
  mono = false,
}: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-border/60 bg-background/60 px-3 py-2">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon size={15} />
      </span>
      <div className="min-w-0">
        <p className="text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">{label}</p>
        <p
          className={`truncate text-[0.8125rem] font-medium text-text ${mono ? 'font-mono' : ''}`}
          title={typeof value === 'string' ? value : undefined}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

/* --------------------------- Stat tiles --------------------------- */

export function StatTile({
  icon: Icon,
  label,
  value,
  hint,
  tone = 'default',
}: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'default' | 'primary' | 'accent';
}) {
  const isAccent = tone === 'accent';
  const isPrimary = tone === 'primary';

  const shell = isAccent
    ? 'border-transparent bg-text text-surface shadow-sm'
    : isPrimary
      ? 'border-primary/20 bg-primary/5'
      : 'border-border/70 bg-surface';

  const labelTone = isAccent ? 'text-surface/70' : 'text-text-muted';
  const iconTone = isAccent ? 'text-surface/80' : isPrimary ? 'text-primary' : 'text-text-muted';
  const hintTone = isAccent ? 'text-surface/70' : 'text-text-muted';

  return (
    <div className={`rounded-2xl border p-3.5 shadow-sm sm:p-4 ${shell}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-[0.625rem] font-semibold uppercase tracking-wider ${labelTone}`}>
          {label}
        </span>
        <Icon size={15} className={iconTone} />
      </div>
      <p className="mt-1.5 text-lg font-bold tabular-nums sm:text-xl">{value}</p>
      {hint && <p className={`mt-0.5 truncate text-[0.6875rem] ${hintTone}`}>{hint}</p>}
    </div>
  );
}

/* ----------------------------- Badges ----------------------------- */

export function StatusPill({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.6875rem] font-semibold ring-1 ring-inset ${
        active
          ? 'bg-success/10 text-success ring-success/25'
          : 'bg-danger/10 text-danger ring-danger/25'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-success' : 'bg-danger'}`} />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

export function Chip({ children, tone = 'primary' }: { children: ReactNode; tone?: 'primary' | 'muted' }) {
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-[0.6875rem] font-medium ${
        tone === 'primary'
          ? 'bg-primary/10 text-primary'
          : 'border border-border/70 bg-background text-text-muted'
      }`}
    >
      {children}
    </span>
  );
}

/* ------------------------------ Photo ----------------------------- */

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return `${parts[0]?.[0] ?? ''}${parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : ''}`.toUpperCase();
}

export function ProfilePhoto({
  src,
  name,
  active = true,
}: {
  src?: string | null;
  name: string;
  active?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const showImage = !!src && !failed;

  return (
    <div className="relative shrink-0 self-start lg:self-center">
      {showImage ? (
        <img
          src={src}
          alt={name}
          onError={() => setFailed(true)}
          className="h-32 w-28 rounded-2xl object-cover shadow-md ring-4 ring-surface sm:h-40 sm:w-32"
        />
      ) : (
        <div className="flex h-32 w-28 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 via-primary/10 to-transparent shadow-md ring-4 ring-surface sm:h-40 sm:w-32">
          <span className="text-3xl font-bold tracking-wide text-primary">{initials(name)}</span>
        </div>
      )}
      <span
        className={`absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-surface shadow-sm ${
          active ? 'bg-success' : 'bg-danger'
        }`}
        aria-hidden="true"
      />
    </div>
  );
}

/* --------------------------- Empty state -------------------------- */

export function EmptyState({ icon: Icon, title, hint }: { icon: LucideIcon; title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-border bg-background/50 px-4 py-8 text-center">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary/70">
        <Icon size={18} />
      </span>
      <p className="text-[0.8125rem] font-medium text-text">{title}</p>
      {hint && <p className="max-w-sm text-xs text-text-muted">{hint}</p>}
    </div>
  );
}

/* --------------------------- User account ------------------------- */

export interface ProfileUser {
  id: string;
  username: string;
  email?: string;
  roles?: string[];
  customRoles?: Array<{ id: string; name: string }>;
  lastLoginAt?: string;
}

export function UserAccountCard({
  user,
  onResendLink,
  onCopyResetLink,
  onChangeUsername,
  onChangePassword,
  onManageRoles,
  resending = false,
  generating = false,
}: {
  user: ProfileUser;
  onResendLink: () => void;
  onCopyResetLink: () => void;
  onChangeUsername: () => void;
  onChangePassword: () => void;
  onManageRoles: () => void;
  resending?: boolean;
  generating?: boolean;
}) {
  const roles = user.roles ?? [];
  const customRoles = user.customRoles ?? [];

  const actions: Array<{
    label: string;
    hint: string;
    icon: LucideIcon;
    onClick: () => void;
    tone: string;
    loading?: boolean;
  }> = [
    {
      label: 'Resend link',
      hint: 'Email a password reset',
      icon: Mail,
      onClick: onResendLink,
      tone: 'bg-primary/10 text-primary',
      loading: resending,
    },
    {
      label: 'Copy reset link',
      hint: 'Copy without sending',
      icon: Copy,
      onClick: onCopyResetLink,
      tone: 'bg-primary/10 text-primary',
      loading: generating,
    },
    {
      label: 'Change username',
      hint: 'Update sign-in name',
      icon: AtSign,
      onClick: onChangeUsername,
      tone: 'bg-secondary/10 text-secondary',
    },
    {
      label: 'Change password',
      hint: 'Set a new password',
      icon: Lock,
      onClick: onChangePassword,
      tone: 'bg-warning/10 text-warning',
    },
    {
      label: 'Manage roles',
      hint: 'Permissions & access',
      icon: Key,
      onClick: onManageRoles,
      tone: 'bg-primary/10 text-primary',
    },
  ];

  return (
    <SectionCard
      icon={ShieldCheck}
      title="System user account"
      subtitle="Sign-in identity and access"
      action={
        <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2.5 py-1 text-[0.6875rem] font-semibold text-success ring-1 ring-inset ring-success/25">
          <span className="h-1.5 w-1.5 rounded-full bg-success" />
          Linked
        </span>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <InfoTile icon={UserRound} label="Username" value={user.username} />
          <InfoTile icon={Mail} label="User email" value={user.email || '—'} />
          <InfoTile
            icon={KeyRound}
            label="Roles"
            value={
              roles.length > 0 ? (
                <span className="flex flex-wrap gap-1">
                  {roles.map((role) => (
                    <Chip key={role}>{role.replace(/_/g, ' ')}</Chip>
                  ))}
                </span>
              ) : (
                'No roles'
              )
            }
          />
          <InfoTile
            icon={ShieldCheck}
            label="Last login"
            value={user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
          />
        </div>

        {customRoles.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[0.6875rem] font-medium text-text-muted">Custom roles:</span>
            {customRoles.map((role) => (
              <Chip key={role.id} tone="muted">
                {role.name}
              </Chip>
            ))}
          </div>
        )}

        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.label}
                type="button"
                onClick={action.onClick}
                disabled={action.loading}
                className="group flex items-center gap-2.5 rounded-xl border border-border/60 bg-background/50 px-3 py-2.5 text-left transition-colors hover:border-primary/30 hover:bg-primary/[0.04] disabled:opacity-50"
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${action.tone}`}
                >
                  {action.loading ? (
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <Icon size={15} />
                  )}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[0.8125rem] font-medium text-text">
                    {action.label}
                  </span>
                  <span className="block truncate text-[0.6875rem] text-text-muted">{action.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </SectionCard>
  );
}

/* ---------------------------- Skeleton ---------------------------- */

export function ProfileSkeleton() {
  return (
    <div className="w-full space-y-4" aria-busy="true" aria-label="Loading profile">
      <div className="flex items-center justify-between">
        <div className="h-9 w-36 animate-pulse rounded-xl bg-border/50" />
        <div className="h-9 w-40 animate-pulse rounded-xl bg-border/50" />
      </div>
      <div className="h-52 animate-pulse rounded-2xl border border-border/70 bg-border/30" />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="h-24 animate-pulse rounded-2xl bg-border/40" />
        ))}
      </div>
      <div className="h-64 animate-pulse rounded-2xl border border-border/70 bg-border/30" />
    </div>
  );
}
